/**
 * NutritionCalculationSettings - Admin settings for calorie calculation
 * Allows admins to configure:
 * - BMR formula (Harris-Benedict vs Mifflin-St Jeor)
 * - Calorie adjustments for different goals (Abnehmen, Erhalten, Aufbauen, Muskelaufbau)
 * - Activity level multipliers
 * - Work/Job activity levels (NEW)
 * - Activity tracking variants (Weekly vs Daily)
 * - Body composition adjustments (InBody ready)
 * - Afterburn effect (EPOC) settings
 * - AI calculation prompts
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { Save, Calculator, Target, Flame, Activity, Loader2, Info, Sparkles, Brain, Scale, Zap, Link, ExternalLink, BarChart3, Briefcase } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SETTINGS = {
  bmr_formula: 'mifflin_st_jeor', // or 'harris_benedict'
  calorie_adjustments: {
    // Legacy goals
    lose_weight: -400,
    maintain: 0,
    gain_weight: 300,
    gain_muscle: 400,
    // New cycle types (percentage-based adjustments converted to kcal at TDEE ~2000)
    aufbau: 300,      // +15% of ~2000 = +300 kcal
    kraft: 100,       // +5% of ~2000 = +100 kcal
    masse: 400,       // +20% of ~2000 = +400 kcal
    definition: -400, // -20% of ~2000 = -400 kcal
    erhaltung: 0      // 0%
  },
  activity_multipliers: {
    sedentary: 1.2,
    light: 1.375,
    moderate: 1.55,
    active: 1.725,
    very_active: 1.9
  },
  // NEW: Work/Job activity levels - bonus calories based on job type
  work_activity_levels: {
    buero: { label: 'Büro / Homeoffice', description: 'Überwiegend sitzend', kcal_bonus: 0, emoji: '💻' },
    leicht_stehend: { label: 'Leicht stehend', description: 'Lehrer, Verkäufer, Empfang', kcal_bonus: 200, emoji: '🧑‍🏫' },
    stehend_gehend: { label: 'Stehend & Gehend', description: 'Kellner, Friseur, Pflege', kcal_bonus: 350, emoji: '🚶' },
    koerperlich_leicht: { label: 'Körperlich leicht', description: 'Handwerk, Einzelhandel', kcal_bonus: 500, emoji: '🔧' },
    koerperlich_mittel: { label: 'Körperlich mittel', description: 'Lager, Gärtner, Mechaniker', kcal_bonus: 650, emoji: '📦' },
    koerperlich_schwer: { label: 'Körperlich schwer', description: 'Bau, Umzug, Landwirtschaft', kcal_bonus: 800, emoji: '🏗️' }
  },
  // Daily activity levels for Variant B
  daily_activity_levels: {
    sehr_schlecht: { label: 'Sehr Schlecht', description: 'Kaum Bewegung, viel sitzen', kcal_adjustment: 0 },
    schlecht: { label: 'Schlecht', description: 'Wenig Bewegung', kcal_adjustment: 30 },
    durchschnittlich: { label: 'Durchschnittlich', description: 'Normale Tagesaktivität', kcal_adjustment: 60 },
    gut: { label: 'Gut', description: 'Aktiver Alltag', kcal_adjustment: 90 },
    sehr_gut: { label: 'Sehr Gut', description: 'Viel Bewegung im Alltag', kcal_adjustment: 120 },
    profi: { label: 'Profi', description: 'Körperlich anspruchsvoller Job', kcal_adjustment: 150 }
  },
  // Activity tracking variant settings
  activity_variant: 'weekly', // 'weekly' (Variant A) or 'daily' (Variant B)
  allow_variant_choice: true, // Allow customer to choose between variants
  // Body composition settings
  body_composition_enabled: true,
  body_fat_adjustments: {
    very_low: { range: '0-12%', kcal_bonus: 100, description: 'Athletisch, sehr niedrig' },
    low: { range: '12-18%', kcal_bonus: 50, description: 'Fit, niedrig' },
    normal: { range: '18-25%', kcal_bonus: 0, description: 'Normal' },
    high: { range: '25-32%', kcal_bonus: -50, description: 'Leicht erhöht' },
    very_high: { range: '32%+', kcal_bonus: -100, description: 'Erhöht' }
  },
  inbody_integration_ready: true, // Prepared for InBody data
  // Afterburn effect (EPOC) settings
  afterburn_enabled: true,
  afterburn_percentages: {
    low_intensity: 5,      // 5% of training calories as afterburn
    medium_intensity: 10,  // 10%
    high_intensity: 15,    // 15%
    hiit: 20               // 20% for HIIT/intense training
  },
  afterburn_duration_hours: 24, // How long afterburn effect lasts
  // AI calculation prompts
  ai_calculation_enabled: true,
  ai_calorie_prompt: `Du bist ein Ernährungsexperte. Berechne den täglichen Kalorienbedarf basierend auf:
- Grundumsatz (BMR) nach Mifflin-St Jeor Formel
- Aktivitätslevel und Trainings
- Körperzusammensetzung (Fettanteil, Muskelmasse)
- Ziel (Aufbau, Definition, Erhaltung)

Berücksichtige:
- Menschen mit weniger Körperfett haben einen höheren Grundumsatz
- Muskelmasse verbrennt mehr Kalorien als Fettgewebe
- Der Nachbrenn-Effekt (EPOC) nach intensivem Training

Gib eine präzise Kalorienempfehlung mit kurzer Begründung.`,
  ai_afterburn_prompt: `Berechne den Nachbrenn-Effekt (EPOC) basierend auf:
- Trainingsart und Intensität
- Trainingsdauer
- Körpergewicht und Fitness-Level

Der Nachbrenn-Effekt beträgt typischerweise:
- Leichtes Training: 5% der verbrannten Kalorien
- Moderates Training: 10%
- Intensives Training: 15%
- HIIT: 20%

Dieser Effekt wirkt bis zu 24 Stunden nach dem Training.`,
  default_activity_level: 'moderate',
  default_macro_preset: 'balanced',
  // Macro distribution presets by fitness goal (percentages)
  // Diese werden im Onboarding verwendet (aufbau, kraft, masse, definition, erhaltung)
  macro_presets_by_goal: {
    aufbau: {
      name: 'Aufbau-Phase',
      protein_percent: 25,
      carbs_percent: 50,
      fat_percent: 25,
      protein_per_kg: 2.0,
      fat_min_per_kg: 0.7,
      description: 'Fokus auf Muskelwachstum mit KH-Überschuss'
    },
    kraft: {
      name: 'Kraft-Phase',
      protein_percent: 30,
      carbs_percent: 45,
      fat_percent: 25,
      protein_per_kg: 2.0,
      fat_min_per_kg: 0.7,
      description: 'Ausgewogen für Krafttraining'
    },
    masse: {
      name: 'Masse-Phase',
      protein_percent: 20,
      carbs_percent: 55,
      fat_percent: 25,
      protein_per_kg: 1.8,
      fat_min_per_kg: 0.7,
      description: 'Maximale Kohlenhydrate für Gewichtszunahme'
    },
    definition: {
      name: 'Definitions-Phase',
      protein_percent: 35,
      carbs_percent: 35,
      fat_percent: 30,
      protein_per_kg: 2.2,
      fat_min_per_kg: 0.8,
      description: 'Hoher Proteinanteil für Muskelerhalt bei Fettverlust'
    },
    erhaltung: {
      name: 'Erhaltung',
      protein_percent: 25,
      carbs_percent: 45,
      fat_percent: 30,
      protein_per_kg: 1.6,
      fat_min_per_kg: 0.8,
      description: 'Standardverteilung für Erhalt'
    }
  },
  // Meal distribution defaults
  meal_distribution: {
    breakfast: 25,
    lunch: 35,
    dinner: 30,
    snacks: 10
  }
};

const GOAL_LABELS = {
  // Legacy goals
  lose_weight: { label: 'Abnehmen', emoji: '⚖️', color: 'bg-blue-100 text-blue-700' },
  maintain: { label: 'Erhalten', emoji: '✅', color: 'bg-green-100 text-green-700' },
  gain_weight: { label: 'Zunehmen', emoji: '📈', color: 'bg-orange-100 text-orange-700' },
  gain_muscle: { label: 'Muskelaufbau', emoji: '💪', color: 'bg-purple-100 text-purple-700' },
  // New cycle types (ERPSE style)
  aufbau: { label: 'Aufbau-Phase', emoji: '🏋️', color: 'bg-amber-100 text-amber-700' },
  kraft: { label: 'Kraft-Phase', emoji: '💪', color: 'bg-orange-100 text-orange-700' },
  masse: { label: 'Masse-Phase', emoji: '📈', color: 'bg-red-100 text-red-700' },
  definition: { label: 'Definitions-Phase', emoji: '🎯', color: 'bg-blue-100 text-blue-700' },
  erhaltung: { label: 'Erhaltung', emoji: '⚖️', color: 'bg-green-100 text-green-700' }
};

const ACTIVITY_LABELS = {
  sedentary: { label: 'Sehr wenig aktiv', description: 'Bürojob, kaum Bewegung' },
  light: { label: 'Leicht aktiv', description: '1-3x Training/Woche' },
  moderate: { label: 'Moderat aktiv', description: '3-5x Training/Woche' },
  active: { label: 'Sehr aktiv', description: '6-7x Training/Woche' },
  very_active: { label: 'Extrem aktiv', description: 'Tägliches intensives Training' }
};

const NutritionCalculationSettings = () => {
  const navigate = useNavigate();
  const [settings, setSettings] = useState(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('grundlagen');
  
  // Service Calorie Rates State (separate from main settings)
  const [serviceRates, setServiceRates] = useState({});
  const [loadingRates, setLoadingRates] = useState(false);
  const [savingRates, setSavingRates] = useState(false);

  useEffect(() => {
    loadSettings();
    loadServiceCalorieRates();
  }, []);

  const loadServiceCalorieRates = async () => {
    setLoadingRates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/portal/nutrition/admin/service-calorie-rates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.rates) {
        setServiceRates(response.data.rates);
      }
    } catch (error) {
      console.log('Using default service rates');
    } finally {
      setLoadingRates(false);
    }
  };

  const saveServiceCalorieRates = async () => {
    setSavingRates(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/nutrition/admin/service-calorie-rates`, 
        { rates: serviceRates },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Trainings-Kalorien gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern der Trainings-Kalorien');
    } finally {
      setSavingRates(false);
    }
  };

  const resetServiceCalorieRates = async () => {
    setSavingRates(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/portal/nutrition/admin/service-calorie-rates/reset`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data?.rates) {
        setServiceRates(response.data.rates);
        toast.success('Auf Standardwerte zurückgesetzt');
      }
    } catch (error) {
      toast.error('Fehler beim Zurücksetzen');
    } finally {
      setSavingRates(false);
    }
  };

  const updateServiceRate = (key, field, value) => {
    setServiceRates(prev => ({
      ...prev,
      [key]: {
        ...prev[key],
        [field]: field === 'kcal_per_min' 
          ? (parseFloat(value) || 0) 
          : field === 'kcal_per_30min' 
            ? (parseInt(value) || 0) 
            : value,
        // Auto-calculate the other value
        ...(field === 'kcal_per_min' ? { kcal_per_30min: Math.round((parseFloat(value) || 0) * 30) } : {}),
        ...(field === 'kcal_per_30min' ? { kcal_per_min: Math.round((parseInt(value) || 0) / 30 * 10) / 10 } : {})
      }
    }));
  };

  const loadSettings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/settings/nutrition-calculation`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data) {
        setSettings({ ...DEFAULT_SETTINGS, ...response.data });
      }
    } catch (error) {
      console.log('Using default settings');
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/settings/nutrition-calculation`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Fehler beim Speichern');
    } finally {
      setSaving(false);
    }
  };

  const updateCalorieAdjustment = (goal, value) => {
    setSettings(prev => ({
      ...prev,
      calorie_adjustments: {
        ...prev.calorie_adjustments,
        [goal]: parseInt(value) || 0
      }
    }));
  };

  const updateActivityMultiplier = (level, value) => {
    setSettings(prev => ({
      ...prev,
      activity_multipliers: {
        ...prev.activity_multipliers,
        [level]: parseFloat(value) || 1.0
      }
    }));
  };

  const updateDailyActivityLevel = (level, field, value) => {
    setSettings(prev => ({
      ...prev,
      daily_activity_levels: {
        ...prev.daily_activity_levels,
        [level]: {
          ...prev.daily_activity_levels[level],
          [field]: field === 'kcal_adjustment' ? parseInt(value) || 0 : value
        }
      }
    }));
  };

  const updateWorkActivityLevel = (level, field, value) => {
    setSettings(prev => ({
      ...prev,
      work_activity_levels: {
        ...prev.work_activity_levels,
        [level]: {
          ...prev.work_activity_levels[level],
          [field]: field === 'kcal_bonus' ? parseInt(value) || 0 : value
        }
      }
    }));
  };

  const updateBodyFatAdjustment = (level, field, value) => {
    setSettings(prev => ({
      ...prev,
      body_fat_adjustments: {
        ...prev.body_fat_adjustments,
        [level]: {
          ...prev.body_fat_adjustments[level],
          [field]: field === 'kcal_bonus' ? parseInt(value) || 0 : value
        }
      }
    }));
  };

  const updateAfterburnPercentage = (intensity, value) => {
    setSettings(prev => ({
      ...prev,
      afterburn_percentages: {
        ...prev.afterburn_percentages,
        [intensity]: parseInt(value) || 0
      }
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      {/* Link to AI Settings */}
      <Card className="bg-gradient-to-r from-purple-50 to-indigo-50 border-purple-200">
        <CardContent className="py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Brain className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="font-medium text-purple-900">KI-Berechnungen konfigurieren</p>
                <p className="text-sm text-purple-700">Passen Sie die KI-Prompts für Kalorien- und Nachbrenn-Berechnung an</p>
              </div>
            </div>
            <Button 
              variant="outline" 
              className="border-purple-300 hover:bg-purple-100"
              onClick={() => navigate('/settings/advanced?tab=ki-api')}
            >
              <Link className="w-4 h-4 mr-2" />
              KI-Einstellungen öffnen
            </Button>
          </div>
        </CardContent>
      </Card>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-7 w-full">
          <TabsTrigger value="grundlagen" className="gap-1">
            <Calculator className="w-4 h-4" />
            <span className="hidden sm:inline">Grundlagen</span>
          </TabsTrigger>
          <TabsTrigger value="training" className="gap-1">
            <Flame className="w-4 h-4" />
            <span className="hidden sm:inline">Training</span>
          </TabsTrigger>
          <TabsTrigger value="makros" className="gap-1">
            <Target className="w-4 h-4" />
            <span className="hidden sm:inline">Makros</span>
          </TabsTrigger>
          <TabsTrigger value="aktivitaet" className="gap-1">
            <Activity className="w-4 h-4" />
            <span className="hidden sm:inline">Aktivität</span>
          </TabsTrigger>
          <TabsTrigger value="koerper" className="gap-1">
            <Scale className="w-4 h-4" />
            <span className="hidden sm:inline">Körper</span>
          </TabsTrigger>
          <TabsTrigger value="nachbrenn" className="gap-1">
            <Zap className="w-4 h-4" />
            <span className="hidden sm:inline">Nachbrenn</span>
          </TabsTrigger>
          <TabsTrigger value="mahlzeiten" className="gap-1">
            <BarChart3 className="w-4 h-4" />
            <span className="hidden sm:inline">Mahlzeiten</span>
          </TabsTrigger>
        </TabsList>

        {/* Grundlagen Tab */}
        <TabsContent value="grundlagen" className="space-y-6 mt-6">{renderGrundlagenTab()}</TabsContent>

        {/* Training Tab (NEU) */}
        <TabsContent value="training" className="space-y-6 mt-6">{renderTrainingTab()}</TabsContent>

        {/* Makros Tab (NEU) */}
        <TabsContent value="makros" className="space-y-6 mt-6">{renderMakrosTab()}</TabsContent>

        {/* Aktivität Tab */}
        <TabsContent value="aktivitaet" className="space-y-6 mt-6">{renderAktivitaetTab()}</TabsContent>

        {/* Körper Tab */}
        <TabsContent value="koerper" className="space-y-6 mt-6">{renderKoerperTab()}</TabsContent>

        {/* Nachbrenn Tab */}
        <TabsContent value="nachbrenn" className="space-y-6 mt-6">{renderNachbrennTab()}</TabsContent>

        {/* Mahlzeiten Tab */}
        <TabsContent value="mahlzeiten" className="space-y-6 mt-6">{renderMahlzeitenTab()}</TabsContent>
      </Tabs>

      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={saveSettings} disabled={saving} className="gap-2">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Alle Einstellungen speichern
        </Button>
      </div>
    </div>
  );

  // Render Functions for each Tab
  
  // NEW: Training Calorie Rates Tab
  function renderTrainingTab() {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Flame className="w-5 h-5 text-orange-600" />
              Trainings-Kalorienraten
            </CardTitle>
            <p className="text-sm text-gray-500">
              Konfigurieren Sie die geschätzten Kalorien pro Trainingsart. Diese Werte werden für alle gebuchten Termine und manuell eingetragenen Trainings verwendet.
            </p>
          </CardHeader>
          <CardContent className="space-y-4">
            {loadingRates ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (
              <>
                <div className="grid gap-4">
                  {Object.entries(serviceRates).map(([key, rate]) => (
                    <div key={key} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                      <div className="text-2xl w-10 text-center">{rate.emoji || '🎯'}</div>
                      <div className="flex-1 min-w-[150px]">
                        <Input 
                          value={rate.label || key}
                          onChange={(e) => updateServiceRate(key, 'label', e.target.value)}
                          className="font-medium"
                          placeholder="Name der Aktivität"
                        />
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-20">
                          <Label className="text-xs text-gray-500">kcal/min</Label>
                          <Input 
                            type="number"
                            value={rate.kcal_per_min || 0}
                            onChange={(e) => updateServiceRate(key, 'kcal_per_min', e.target.value)}
                            min={0}
                            max={50}
                            step={0.5}
                          />
                        </div>
                        <span className="text-gray-400 pt-5">=</span>
                        <div className="w-24">
                          <Label className="text-xs text-gray-500">kcal/30min</Label>
                          <Input 
                            type="number"
                            value={rate.kcal_per_30min || 0}
                            onChange={(e) => updateServiceRate(key, 'kcal_per_30min', e.target.value)}
                            min={0}
                            max={1500}
                            step={10}
                          />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="flex items-center justify-between pt-4 border-t">
                  <Button 
                    variant="outline" 
                    onClick={resetServiceCalorieRates}
                    disabled={savingRates}
                    className="text-gray-600"
                  >
                    Auf Standardwerte zurücksetzen
                  </Button>
                  <Button 
                    onClick={saveServiceCalorieRates} 
                    disabled={savingRates}
                    className="gap-2 bg-orange-600 hover:bg-orange-700"
                  >
                    {savingRates ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Trainings-Kalorien speichern
                  </Button>
                </div>
              </>
            )}
          </CardContent>
        </Card>

        {/* Info Box */}
        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="py-4">
            <div className="flex gap-3">
              <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-800">
                <p className="font-medium mb-1">Wie werden die Kalorien berechnet?</p>
                <ul className="list-disc list-inside space-y-1 text-blue-700">
                  <li>Bei <strong>gebuchten Terminen</strong>: Der Service-Name wird mit den Keywords oben abgeglichen</li>
                  <li>Bei <strong>manuellen Einträgen</strong>: Der Kunde wählt die Aktivität beim Eintragen</li>
                  <li>Änderungen wirken sich auf alle <strong>neuen</strong> Einträge aus</li>
                  <li>Bereits erfasste Trainings behalten ihre ursprünglichen Werte</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  function renderGrundlagenTab() {
    return (
      <>
        {/* BMR Formula Selection */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Calculator className="w-5 h-5 text-blue-600" />
              Grundumsatz-Formel (BMR)
            </CardTitle>
            <CardDescription>
              Wählen Sie die Formel zur Berechnung des Grundumsatzes
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  settings.bmr_formula === 'mifflin_st_jeor'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSettings(prev => ({ ...prev, bmr_formula: 'mifflin_st_jeor' }))}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Mifflin-St Jeor</span>
                  <Badge variant="outline" className="bg-green-50 text-green-700">Empfohlen</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Moderne Formel (1990), genauer für heutige Bevölkerung
                </p>
                <div className="mt-2 text-xs text-gray-500 font-mono">
                  ♂: (10 × kg) + (6.25 × cm) - (5 × Alter) + 5<br/>
                  ♀: (10 × kg) + (6.25 × cm) - (5 × Alter) - 161
                </div>
              </div>
              
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  settings.bmr_formula === 'harris_benedict'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSettings(prev => ({ ...prev, bmr_formula: 'harris_benedict' }))}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Harris-Benedict</span>
                  <Badge variant="outline">Klassisch</Badge>
                </div>
                <p className="text-sm text-gray-600">
                  Klassische Formel (1918/1984), weit verbreitet
                </p>
                <div className="mt-2 text-xs text-gray-500 font-mono">
                  ♂: 88.36 + (13.4 × kg) + (4.8 × cm) - (5.7 × Alter)<br/>
                  ♀: 447.6 + (9.2 × kg) + (3.1 × cm) - (4.3 × Alter)
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Calorie Adjustments */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Target className="w-5 h-5 text-orange-600" />
              Kalorien-Anpassungen nach Ziel
            </CardTitle>
            <CardDescription>
              Wie viele Kalorien werden zum TDEE addiert/subtrahiert basierend auf dem Ziel
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {Object.entries(GOAL_LABELS).map(([goal, { label, emoji, color }]) => (
                <div key={goal} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                  <div className={`px-2 py-1 rounded text-sm font-medium ${color}`}>
                    {emoji} {label}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={settings.calorie_adjustments[goal]}
                        onChange={(e) => updateCalorieAdjustment(goal, e.target.value)}
                        className="w-24 text-center font-medium"
                        step="50"
                      />
                      <span className="text-sm text-gray-500">kcal</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-700">
                <strong>Beispiel:</strong> Bei einem TDEE von 2000 kcal und Ziel &quot;Abnehmen&quot; mit -400 kcal 
                wird das tägliche Kalorienziel auf 1600 kcal gesetzt.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Default Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Flame className="w-5 h-5 text-red-600" />
              Standard-Einstellungen für neue Kunden
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label className="mb-2 block">Standard-Aktivitätslevel</Label>
                <Select
                  value={settings.default_activity_level}
                  onValueChange={(v) => setSettings(prev => ({ ...prev, default_activity_level: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(ACTIVITY_LABELS).map(([level, { label }]) => (
                      <SelectItem key={level} value={level}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="mb-2 block">Standard-Makro-Preset</Label>
                <Select
                  value={settings.default_macro_preset}
                  onValueChange={(v) => setSettings(prev => ({ ...prev, default_macro_preset: v }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="balanced">Ausgewogen (25/50/25)</SelectItem>
                    <SelectItem value="high_protein">High Protein (35/40/25)</SelectItem>
                    <SelectItem value="low_carb">Low Carb (30/25/45)</SelectItem>
                    <SelectItem value="keto">Keto (25/5/70)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  // NEU: Makros Tab - Makronährstoff-Verteilungen nach Ziel
  function renderMakrosTab() {
    const macroPresets = settings.macro_presets_by_goal || DEFAULT_SETTINGS.macro_presets_by_goal;
    
    const updateMacroPreset = (goalKey, field, value) => {
      setSettings(prev => ({
        ...prev,
        macro_presets_by_goal: {
          ...prev.macro_presets_by_goal,
          [goalKey]: {
            ...prev.macro_presets_by_goal[goalKey],
            [field]: field.includes('percent') || field.includes('per_kg') ? parseFloat(value) || 0 : value
          }
        }
      }));
    };
    
    // Nur die 5 Phasen, die im Onboarding verwendet werden
    const goalGroups = {
      'Trainings-Phasen (Onboarding)': ['aufbau', 'kraft', 'masse', 'definition', 'erhaltung']
    };
    
    return (
      <>
        {/* Info Banner */}
        <Card className="border-2 border-blue-200 bg-blue-50/30">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5" />
              <div>
                <p className="font-medium text-blue-900">Makronährstoff-Verteilungen nach Ziel</p>
                <p className="text-sm text-blue-700 mt-1">
                  Die Makroverteilung unterscheidet sich nicht grundsätzlich zwischen Mann und Frau. 
                  Unterschiede ergeben sich aus <strong>Körpergewicht, Muskelmasse und Aktivitätslevel</strong>.
                </p>
                <div className="mt-2 text-xs text-blue-600 flex gap-4">
                  <span>Protein: 1g = 4 kcal</span>
                  <span>Kohlenhydrate: 1g = 4 kcal</span>
                  <span>Fett: 1g = 9 kcal</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {Object.entries(goalGroups).map(([groupName, goalKeys]) => (
          <Card key={groupName}>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Target className="w-5 h-5 text-purple-600" />
                {groupName}
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {goalKeys.map(goalKey => {
                const preset = macroPresets[goalKey] || {};
                const goalInfo = GOAL_LABELS[goalKey] || { label: goalKey, emoji: '🎯', color: 'bg-gray-100' };
                const totalPercent = (preset.protein_percent || 0) + (preset.carbs_percent || 0) + (preset.fat_percent || 0);
                
                return (
                  <div key={goalKey} className="p-4 bg-zinc-50 rounded-lg border space-y-4">
                    {/* Header */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <span className="text-xl">{goalInfo.emoji}</span>
                        <span className="font-semibold">{preset.name || goalInfo.label}</span>
                        <Badge className={goalInfo.color}>{goalKey}</Badge>
                      </div>
                      <Badge variant={totalPercent === 100 ? "success" : "destructive"} className={totalPercent === 100 ? "bg-green-100 text-green-700" : ""}>
                        {totalPercent}%
                      </Badge>
                    </div>
                    
                    {/* Description */}
                    <Input
                      value={preset.description || ''}
                      onChange={(e) => updateMacroPreset(goalKey, 'description', e.target.value)}
                      placeholder="Beschreibung..."
                      className="text-sm"
                    />
                    
                    {/* Macro Percentages */}
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label className="text-xs text-zinc-500 flex items-center gap-1">
                          🥩 Protein (%)
                        </Label>
                        <Input
                          type="number"
                          min="10"
                          max="50"
                          value={preset.protein_percent || ''}
                          onChange={(e) => updateMacroPreset(goalKey, 'protein_percent', e.target.value)}
                          className="text-center font-medium"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500 flex items-center gap-1">
                          🍞 Kohlenhydrate (%)
                        </Label>
                        <Input
                          type="number"
                          min="10"
                          max="70"
                          value={preset.carbs_percent || ''}
                          onChange={(e) => updateMacroPreset(goalKey, 'carbs_percent', e.target.value)}
                          className="text-center font-medium"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500 flex items-center gap-1">
                          🥑 Fett (%)
                        </Label>
                        <Input
                          type="number"
                          min="15"
                          max="45"
                          value={preset.fat_percent || ''}
                          onChange={(e) => updateMacroPreset(goalKey, 'fat_percent', e.target.value)}
                          className="text-center font-medium"
                        />
                      </div>
                    </div>
                    
                    {/* Per-KG Values */}
                    <div className="grid grid-cols-2 gap-4 pt-2 border-t">
                      <div>
                        <Label className="text-xs text-zinc-500">Protein pro kg Körpergewicht (g)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="1.2"
                          max="3.0"
                          value={preset.protein_per_kg || ''}
                          onChange={(e) => updateMacroPreset(goalKey, 'protein_per_kg', e.target.value)}
                          placeholder="z.B. 2.0"
                        />
                        <p className="text-xs text-zinc-400 mt-1">Standard: 1.6-2.2g für Muskelaufbau</p>
                      </div>
                      <div>
                        <Label className="text-xs text-zinc-500">Fett Minimum pro kg (g)</Label>
                        <Input
                          type="number"
                          step="0.1"
                          min="0.5"
                          max="1.5"
                          value={preset.fat_min_per_kg || ''}
                          onChange={(e) => updateMacroPreset(goalKey, 'fat_min_per_kg', e.target.value)}
                          placeholder="z.B. 0.8"
                        />
                        <p className="text-xs text-zinc-400 mt-1">Wichtig für Hormone (min. 0.8g)</p>
                      </div>
                    </div>
                    
                    {/* Warning if not 100% */}
                    {totalPercent !== 100 && (
                      <p className="text-sm text-orange-600 bg-orange-50 p-2 rounded">
                        ⚠️ Die Summe sollte 100% ergeben (aktuell: {totalPercent}%)
                      </p>
                    )}
                  </div>
                );
              })}
            </CardContent>
          </Card>
        ))}

        {/* Quick Reference */}
        <Card className="border-zinc-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Info className="w-5 h-5 text-zinc-500" />
              Schnellübersicht: Empfohlene Werte
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-zinc-100">
                    <th className="p-2 text-left">Phase</th>
                    <th className="p-2 text-center">Protein</th>
                    <th className="p-2 text-center">Kohlenhydrate</th>
                    <th className="p-2 text-center">Fett</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-t">
                    <td className="p-2 font-medium">🏋️ Aufbau</td>
                    <td className="p-2 text-center">25%</td>
                    <td className="p-2 text-center text-blue-600">50%</td>
                    <td className="p-2 text-center">25%</td>
                  </tr>
                  <tr className="border-t bg-zinc-50">
                    <td className="p-2 font-medium">💪 Kraft</td>
                    <td className="p-2 text-center text-green-600">30%</td>
                    <td className="p-2 text-center">45%</td>
                    <td className="p-2 text-center">25%</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">📈 Masse</td>
                    <td className="p-2 text-center">20%</td>
                    <td className="p-2 text-center text-blue-600">55%</td>
                    <td className="p-2 text-center">25%</td>
                  </tr>
                  <tr className="border-t bg-zinc-50">
                    <td className="p-2 font-medium">🎯 Definition</td>
                    <td className="p-2 text-center text-green-600">35%</td>
                    <td className="p-2 text-center">35%</td>
                    <td className="p-2 text-center">30%</td>
                  </tr>
                  <tr className="border-t">
                    <td className="p-2 font-medium">⚖️ Erhaltung</td>
                    <td className="p-2 text-center">25%</td>
                    <td className="p-2 text-center">45%</td>
                    <td className="p-2 text-center">30%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-xs text-zinc-500 mt-3">
              💡 <strong>Wichtiger als Prozentangaben:</strong> Kalorienbilanz, Protein pro kg Körpergewicht, Trainingsvolumen, Schlaf & Regeneration
            </p>
          </CardContent>
        </Card>
      </>
    );
  }

  function renderAktivitaetTab() {
    return (
      <>
        {/* Activity Variant Selection */}
        <Card className="border-2 border-amber-200 bg-amber-50/30">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5 text-amber-600" />
              Aktivitäts-Tracking Variante
            </CardTitle>
            <CardDescription>
              Wählen Sie, wie die Aktivität der Kunden erfasst werden soll
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-white rounded-lg border">
              <div>
                <Label className="font-medium">Kunde kann Variante wählen</Label>
                <p className="text-sm text-gray-500">Erlaubt dem Kunden zwischen beiden Varianten zu wählen</p>
              </div>
              <Switch
                checked={settings.allow_variant_choice}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, allow_variant_choice: v }))}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Variant A - Weekly */}
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  settings.activity_variant === 'weekly'
                    ? 'border-green-500 bg-green-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSettings(prev => ({ ...prev, activity_variant: 'weekly' }))}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Variante A: Wöchentlich</span>
                  <Badge className="bg-green-500">Primär</Badge>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Training ist bereits in der Wochenberechnung inkludiert
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>✓ Einfache Einrichtung</li>
                  <li>✓ Basiert auf Trainingsfrequenz pro Woche</li>
                  <li>✓ Ideal für konstante Trainingsroutinen</li>
                </ul>
              </div>

              {/* Variant B - Daily */}
              <div
                className={`p-4 border-2 rounded-lg cursor-pointer transition-all ${
                  settings.activity_variant === 'daily'
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
                onClick={() => setSettings(prev => ({ ...prev, activity_variant: 'daily' }))}
              >
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold">Variante B: Täglich</span>
                  <Badge variant="outline" className="border-blue-500 text-blue-600">Detailliert</Badge>
                </div>
                <p className="text-sm text-gray-600 mb-3">
                  Alltag + Trainings separat erfassen mit Nachbrenn-Effekt
                </p>
                <ul className="text-xs text-gray-500 space-y-1">
                  <li>✓ Tägliche Alltagsaktivität bewerten</li>
                  <li>✓ Trainings werden separat addiert</li>
                  <li>✓ Nachbrenn-Effekt vom Vortag</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sedentary Baseline Factor (für Variante B) */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Scale className="w-5 h-5 text-blue-600" />
              Basis-Faktor (BMR × Faktor)
            </CardTitle>
            <CardDescription>
              Der Grundumsatz wird mit diesem Faktor multipliziert als Ausgangsbasis. Standard: 1.2 (sitzend). Wird bei Variante B &quot;Täglich&quot; verwendet.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <Label className="text-sm font-medium">Sedentary Baseline (BMR × ...)</Label>
                <p className="text-xs text-gray-500 mb-2">Basis ohne jegliche Bewegung</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm">BMR ×</span>
                  <Input
                    type="number"
                    value={settings.sedentary_baseline_factor || 1.2}
                    onChange={(e) => setSettings(prev => ({ ...prev, sedentary_baseline_factor: parseFloat(e.target.value) }))}
                    className="w-24 text-center font-medium"
                    step="0.05"
                    min="1.0"
                    max="1.5"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Beispiel: BMR 1730 × 1.2 = 2076 kcal Basis</p>
              </div>
              <div className="p-3 bg-orange-50 rounded-lg">
                <Label className="text-sm font-medium">Max. NEAT-Faktor (Obergrenze)</Label>
                <p className="text-xs text-gray-500 mb-2">Maximaler Alltags-Multiplikator (Schritte)</p>
                <div className="flex items-center gap-2">
                  <span className="text-sm">BMR ×</span>
                  <Input
                    type="number"
                    value={settings.neat_max_factor || 1.75}
                    onChange={(e) => setSettings(prev => ({ ...prev, neat_max_factor: parseFloat(e.target.value) }))}
                    className="w-24 text-center font-medium"
                    step="0.05"
                    min="1.3"
                    max="2.5"
                  />
                </div>
                <p className="text-xs text-gray-400 mt-1">Verhindert unrealistische Werte bei vielen Schritten</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Variant A: Weekly Activity Multipliers */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <BarChart3 className="w-5 h-5 text-green-600" />
              Variante A: Wöchentliche Aktivitätslevel-Multiplikatoren
            </CardTitle>
            <CardDescription>
              PAL-Werte basierend auf Trainingsfrequenz pro Woche (Training inkludiert)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(ACTIVITY_LABELS).map(([level, { label, description }]) => (
                <div key={level} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <span className="font-medium">{label}</span>
                    <p className="text-xs text-gray-500">{description}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">×</span>
                    <Input
                      type="number"
                      value={settings.activity_multipliers[level]}
                      onChange={(e) => updateActivityMultiplier(level, e.target.value)}
                      className="w-20 text-center font-medium"
                      step="0.025"
                      min="1"
                      max="2.5"
                    />
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* NEW: Work/Job Activity Levels */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Briefcase className="w-5 h-5 text-yellow-600" />
              Arbeits-/Berufsaktivität
            </CardTitle>
            <CardDescription>
              Zusätzlicher Kalorienverbrauch basierend auf dem Beruf (wird bei Variante A &quot;Wöchentlich&quot; abgefragt)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(settings.work_activity_levels || DEFAULT_SETTINGS.work_activity_levels).map(([level, data]) => (
                <div key={level} className="flex items-center gap-4 p-3 bg-yellow-50 rounded-lg">
                  <div className="text-2xl">{data.emoji}</div>
                  <div className="flex-1">
                    <Input
                      value={data.label}
                      onChange={(e) => updateWorkActivityLevel(level, 'label', e.target.value)}
                      className="font-medium mb-1"
                    />
                    <Input
                      value={data.description}
                      onChange={(e) => updateWorkActivityLevel(level, 'description', e.target.value)}
                      className="text-xs"
                      placeholder="Beschreibung"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">+</span>
                    <Input
                      type="number"
                      value={data.kcal_bonus}
                      onChange={(e) => updateWorkActivityLevel(level, 'kcal_bonus', e.target.value)}
                      className="w-20 text-center font-medium"
                      step="50"
                    />
                    <span className="text-sm text-gray-500">kcal</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-yellow-50 rounded-lg flex items-start gap-2 border border-yellow-200">
              <Info className="w-4 h-4 text-yellow-700 mt-0.5" />
              <p className="text-sm text-yellow-800">
                <strong>Berechnung:</strong> TDEE (Grundumsatz × Sport-Faktor) + Arbeits-Bonus = Gesamtkalorienbedarf
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Variant B: Daily Activity Levels */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Activity className="w-5 h-5 text-blue-600" />
              Variante B: Tägliche Alltagsaktivität
            </CardTitle>
            <CardDescription>
              Kcal-Anpassungen für tägliche Alltagsaktivität (Trainings werden separat addiert)
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {Object.entries(settings.daily_activity_levels || DEFAULT_SETTINGS.daily_activity_levels).map(([level, data]) => (
                <div key={level} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                  <div className="flex-1">
                    <Input
                      value={data.label}
                      onChange={(e) => updateDailyActivityLevel(level, 'label', e.target.value)}
                      className="font-medium mb-1"
                    />
                    <Input
                      value={data.description}
                      onChange={(e) => updateDailyActivityLevel(level, 'description', e.target.value)}
                      className="text-xs"
                      placeholder="Beschreibung"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-500">+</span>
                    <Input
                      type="number"
                      value={data.kcal_adjustment}
                      onChange={(e) => updateDailyActivityLevel(level, 'kcal_adjustment', e.target.value)}
                      className="w-20 text-center font-medium"
                      step="10"
                    />
                    <span className="text-sm text-gray-500">kcal</span>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 p-3 bg-blue-50 rounded-lg flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-600 mt-0.5" />
              <p className="text-sm text-blue-700">
                <strong>Variante B Berechnung:</strong> Grundumsatz + Alltagsaktivität + Trainings-Kalorien + Nachbrenn-Effekt (Vortag)
              </p>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }

  function renderKoerperTab() {
    return (
      <>
        {/* Body Composition Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Scale className="w-5 h-5 text-purple-600" />
              Körperzusammensetzung berücksichtigen
            </CardTitle>
            <CardDescription>
              Passen Sie den Kalorienbedarf basierend auf dem Körperfettanteil an
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Körperzusammensetzung aktivieren</Label>
                <p className="text-sm text-gray-500">Berücksichtigt Körperfett bei der Berechnung</p>
              </div>
              <Switch
                checked={settings.body_composition_enabled}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, body_composition_enabled: v }))}
              />
            </div>

            {settings.body_composition_enabled && (
              <div className="space-y-3">
                <Label className="text-sm font-medium text-gray-700">Kalorien-Anpassung nach Körperfettanteil</Label>
                {Object.entries(settings.body_fat_adjustments || DEFAULT_SETTINGS.body_fat_adjustments).map(([level, data]) => (
                  <div key={level} className="flex items-center gap-4 p-3 bg-gray-50 rounded-lg">
                    <div className="w-24">
                      <Badge variant="outline" className="whitespace-nowrap">{data.range}</Badge>
                    </div>
                    <div className="flex-1">
                      <Input
                        value={data.description}
                        onChange={(e) => updateBodyFatAdjustment(level, 'description', e.target.value)}
                        className="text-sm"
                        placeholder="Beschreibung"
                      />
                    </div>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={data.kcal_bonus}
                        onChange={(e) => updateBodyFatAdjustment(level, 'kcal_bonus', e.target.value)}
                        className="w-20 text-center font-medium"
                        step="10"
                      />
                      <span className="text-sm text-gray-500">kcal</span>
                    </div>
                  </div>
                ))}
                <div className="p-3 bg-purple-50 rounded-lg flex items-start gap-2">
                  <Info className="w-4 h-4 text-purple-600 mt-0.5" />
                  <p className="text-sm text-purple-700">
                    <strong>Logik:</strong> Menschen mit weniger Körperfett haben mehr Muskelmasse und einen höheren Grundumsatz. 
                    Menschen mit höherem Fettanteil haben einen niedrigeren Stoffwechsel.
                  </p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

      </>
    );
  }

  function renderNachbrennTab() {
    return (
      <>
        {/* Afterburn Settings */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-lg">
              <Zap className="w-5 h-5 text-orange-600" />
              Nachbrenn-Effekt (EPOC)
            </CardTitle>
            <CardDescription>
              Einstellungen für den Nachbrenn-Effekt nach dem Training (nur bei Variante B)
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <Label className="font-medium">Nachbrenn-Effekt aktivieren</Label>
                <p className="text-sm text-gray-500">Zeigt zusätzliche Kalorien vom Vortags-Training an</p>
              </div>
              <Switch
                checked={settings.afterburn_enabled}
                onCheckedChange={(v) => setSettings(prev => ({ ...prev, afterburn_enabled: v }))}
              />
            </div>

            {settings.afterburn_enabled && (
              <>
                <div>
                  <Label className="mb-2 block">Nachbrenn-Dauer (Stunden)</Label>
                  <Input
                    type="number"
                    value={settings.afterburn_duration_hours}
                    onChange={(e) => setSettings(prev => ({ ...prev, afterburn_duration_hours: parseInt(e.target.value) || 24 }))}
                    className="w-32"
                    min="12"
                    max="48"
                  />
                  <p className="text-xs text-gray-500 mt-1">Wie lange der Nachbrenn-Effekt anhält (Standard: 24h)</p>
                </div>

                <div className="space-y-3">
                  <Label className="text-sm font-medium text-gray-700">Nachbrenn-Prozentsätze nach Trainingsintensität</Label>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div className="p-3 bg-green-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-green-700">Leicht</span>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={settings.afterburn_percentages?.low_intensity || 5}
                            onChange={(e) => updateAfterburnPercentage('low_intensity', e.target.value)}
                            className="w-16 text-center"
                            min="0"
                            max="30"
                          />
                          <span className="text-sm">%</span>
                        </div>
                      </div>
                      <p className="text-xs text-green-600">Yoga, Spazieren, leichtes Dehnen</p>
                    </div>

                    <div className="p-3 bg-yellow-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-yellow-700">Moderat</span>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={settings.afterburn_percentages?.medium_intensity || 10}
                            onChange={(e) => updateAfterburnPercentage('medium_intensity', e.target.value)}
                            className="w-16 text-center"
                            min="0"
                            max="30"
                          />
                          <span className="text-sm">%</span>
                        </div>
                      </div>
                      <p className="text-xs text-yellow-600">Joggen, Radfahren, Schwimmen</p>
                    </div>

                    <div className="p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-orange-700">Intensiv</span>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={settings.afterburn_percentages?.high_intensity || 15}
                            onChange={(e) => updateAfterburnPercentage('high_intensity', e.target.value)}
                            className="w-16 text-center"
                            min="0"
                            max="30"
                          />
                          <span className="text-sm">%</span>
                        </div>
                      </div>
                      <p className="text-xs text-orange-600">Krafttraining, schnelles Laufen</p>
                    </div>

                    <div className="p-3 bg-red-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-red-700">HIIT / EMS</span>
                        <div className="flex items-center gap-2">
                          <Input
                            type="number"
                            value={settings.afterburn_percentages?.hiit || 20}
                            onChange={(e) => updateAfterburnPercentage('hiit', e.target.value)}
                            className="w-16 text-center"
                            min="0"
                            max="30"
                          />
                          <span className="text-sm">%</span>
                        </div>
                      </div>
                      <p className="text-xs text-red-600">HIIT, EMS-Training, Crossfit</p>
                    </div>
                  </div>
                </div>

                <div className="p-3 bg-orange-50 rounded-lg flex items-start gap-2">
                  <Zap className="w-4 h-4 text-orange-600 mt-0.5" />
                  <div className="text-sm text-orange-700">
                    <strong>Beispiel:</strong> 30 Min intensives Training (500 kcal) × 15% Nachbrenn = 75 kcal extra am nächsten Tag.
                    <br/>
                    <span className="text-xs">Angezeigt als &quot;Nachbrenn-Effekt vom Vortag&quot;</span>
                  </div>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      </>
    );
  }

  // Mahlzeiten Tab - Standard Mahlzeiten-Verteilung
  function renderMahlzeitenTab() {
    return (
      <>
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-purple-600" />
              Standard Mahlzeiten-Verteilung
            </CardTitle>
            <CardDescription>
              Standard-Verteilung der täglichen Kalorien auf die Mahlzeiten für neue Kunden
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Frühstück (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.meal_distribution?.breakfast || 25}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    meal_distribution: { ...prev.meal_distribution, breakfast: parseInt(e.target.value) || 0 }
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Mittagessen (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.meal_distribution?.lunch || 35}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    meal_distribution: { ...prev.meal_distribution, lunch: parseInt(e.target.value) || 0 }
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Abendessen (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.meal_distribution?.dinner || 30}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    meal_distribution: { ...prev.meal_distribution, dinner: parseInt(e.target.value) || 0 }
                  }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Snacks (%)</Label>
                <Input
                  type="number"
                  min="0"
                  max="100"
                  value={settings.meal_distribution?.snacks || 10}
                  onChange={(e) => setSettings(prev => ({
                    ...prev,
                    meal_distribution: { ...prev.meal_distribution, snacks: parseInt(e.target.value) || 0 }
                  }))}
                />
              </div>
            </div>
            
            {/* Validation Warning */}
            {(() => {
              const total = (settings.meal_distribution?.breakfast || 25) + 
                           (settings.meal_distribution?.lunch || 35) + 
                           (settings.meal_distribution?.dinner || 30) + 
                           (settings.meal_distribution?.snacks || 10);
              if (total !== 100) {
                return (
                  <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
                    ⚠️ Die Summe aller Mahlzeiten beträgt {total}% (sollte 100% sein)
                  </div>
                );
              }
              return (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  ✓ Die Verteilung ergibt 100%
                </div>
              );
            })()}
            
            <div className="p-4 bg-gray-50 rounded-lg text-sm text-gray-600">
              <p className="font-medium mb-2">Hinweis:</p>
              <p>
                Diese Einstellungen gelten als Standard für alle neuen Kunden. Bestehende Kunden mit 
                individuellen Einstellungen werden nicht beeinflusst. Sie können die Werte für einzelne 
                Kunden in deren Profil oder unter &quot;Kunden → Details → Ernährung&quot; anpassen.
              </p>
            </div>
          </CardContent>
        </Card>
      </>
    );
  }
};

export default NutritionCalculationSettings;
